import { CourseDto } from "@/routes/courses/types/coursesDTO";
import { CoursesFilter } from "@/routes/courses/types/coursesFilter";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { Level } from "../enums/level";
import { apiEndpoints } from "@/services/api/api.ts";
import { CoursesLoaderData } from "@/routes/courses/types/coursesLoaderData.ts";
import { ApiResponse } from "@/types/apiTypes/apiResponse";
import { BasePaginatedResponse } from "@/types/apiTypes/basePaginatedReponse";
import { BaseFilterOptionResponse } from "@/types/apiTypes/baseFilterOptionResponse";
import { authService } from "@/services/auth/authService.ts";

export const coursesLoader: LoaderFunction = async ({
  request,
}: LoaderFunctionArgs): Promise<CoursesLoaderData> => {
  try {
    const isAuthenticated = !!authService.getAccessToken();
    const url = new URL(request.url);
    const isInternalFetch = url.searchParams.get("_internal") === "true";
    const requestedPage = url.searchParams.get("page")
      ? Number(url.searchParams.get("page"))
      : 1;

    // Internal fetch değilse (direkt sayfa yüklemesi), page parametresini yok say
    const actualPage = isInternalFetch ? requestedPage : 1;

    const filters: CoursesFilter = {
      categories: url.searchParams.getAll("category"),
      tags: url.searchParams.getAll("tag"),
      minPrice: url.searchParams.get("minPrice")
        ? Number(url.searchParams.get("minPrice"))
        : null,
      maxPrice: url.searchParams.get("maxPrice")
        ? Number(url.searchParams.get("maxPrice"))
        : null,
      search: url.searchParams.get("search") || "",
      levels: url.searchParams.getAll("level").map((l) => Number(l) as Level),
      pageNumber: actualPage,
      pageSize: url.searchParams.get("pageSize")
        ? Number(url.searchParams.get("pageSize"))
        : 5,
      desc: true,
      orderBy: "CreatedAt",
    };

    // API çağrılarını hazırla
    const promises: [
      Promise<ApiResponse<BasePaginatedResponse<CourseDto>>>,
      ...Array<
        Promise<ApiResponse<BaseFilterOptionResponse[]> | ApiResponse<number>>
      >,
    ] = [
      isAuthenticated
        ? apiEndpoints.getCoursesPaginated(filters)
        : apiEndpoints.getPublicCoursesPaginated(filters),
    ];

    // Sadece ilk yükleme ise (internal fetch değilse) filter seçeneklerini de çek
    if (!isInternalFetch) {
      promises.push(
        isAuthenticated
          ? apiEndpoints.getCategoriesForFilter()
          : apiEndpoints.getPublicCategoriesForFilter(),
        isAuthenticated
          ? apiEndpoints.getTagsForFilter()
          : apiEndpoints.getPublicTagsForFilter(),
        isAuthenticated
          ? apiEndpoints.getMaxCoursePrice()
          : apiEndpoints.getPublicMaxCoursePrice(),
      );
    }

    const results = await Promise.allSettled(promises);

    // Kurs verilerini al
    const coursesResponse = results[0];
    if (coursesResponse.status === "rejected") {
      throw new Error("Kurslar yüklenemedi");
    }

    const coursesData = coursesResponse.value.data;

    const loaderData: CoursesLoaderData = {
      courses: {
        currentPage: coursesData.currentPage,
        totalPageCount: coursesData.totalPageCount,
        totalItemCount: coursesData.totalItemCount,
        hasNextPage: coursesData.hasNextPage,
        items: coursesData.items,
      },
    };

    // Filter seçeneklerini ekle (sadece ilk yüklemede)
    if (!isInternalFetch && results.length > 1) {
      const categoriesResponse = results[1];
      const tagsResponse = results[2];
      const maxPriceResponse = results[3];

      loaderData.filterOptions = {
        categories:
          categoriesResponse.status === "fulfilled" &&
          categoriesResponse.value.success
            ? categoriesResponse.value.data
            : [],
        tags:
          tagsResponse.status === "fulfilled" && tagsResponse.value.success
            ? tagsResponse.value.data
            : [],
      };

      // Max price'ı ekle
      if (
        maxPriceResponse &&
        maxPriceResponse.status === "fulfilled" &&
        maxPriceResponse.value.success
      ) {
        loaderData.maxPrice = maxPriceResponse.value.data;
      }
    }

    return loaderData;
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : "Unknown error");
  }
};
